import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Typography,
  Modal,
  Form,
  Row,
  Col,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import categoryService from '../services/categoryService';
import { Category } from '../types/categories';
import CategoryForm from '../components/categories/CategoryForm';
import { useSnackbar } from 'notistack';

const { Title } = Typography;

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await categoryService.getCategories();
      setCategories(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Effect để fill dữ liệu vào form khi currentCategory thay đổi
  useEffect(() => {
    if (currentCategory && isEditModalVisible) {
      const formValues = {
        name: currentCategory.name,
        description: currentCategory.description,
        image: currentCategory.image,
        is_visible: true,
        name_en: currentCategory.translations?.find((t: any) => t.type === 'en')?.name || '',
        description_en: currentCategory.translations?.find((t: any) => t.type === 'en')?.description || '',
        translations: currentCategory.translations || []
      };
      editForm.setFieldsValue(formValues);
    }
  }, [currentCategory, isEditModalVisible, editForm]);

  const showCreateModal = () => {
    createForm.resetFields();
    setIsCreateModalVisible(true);
  };

  const showEditModal = (category: Category) => {
    setCurrentCategory(category);
    setIsEditModalVisible(true);
    // Reset form để đảm bảo dữ liệu mới được load
    editForm.resetFields();
  };

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    try {
      setLoading(true);
      await categoryService.deleteCategory(categoryToDelete._id);
      enqueueSnackbar('Xóa danh mục thành công!', { variant: 'success' });
      setIsDeleteModalVisible(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (err) {
      enqueueSnackbar('Xóa danh mục thất bại!', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setCategoryToDelete(null);
  };

  const handleCreateModalOk = async () => {
    try {
      const values = await createForm.validateFields();
      
      // Structure data with translations
      const categoryData = {
        name: values.name,
        description: values.description,
        image: values.image,
        is_visible: values.is_visible ?? true,
        translations: [
          {
            type: "en",
            name: values.name_en || '',
            description: values.description_en || ''
          }
        ]
      };
      
      const res = await categoryService.createCategory(categoryData);
      if (res.status === 201) {
        setIsCreateModalVisible(false);
        createForm.resetFields();
        fetchCategories();
        enqueueSnackbar('Tạo danh mục thành công!', { variant: 'success' });
      } else {
        enqueueSnackbar('Tạo danh mục thất bại!', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Tạo danh mục thất bại!', { variant: 'error' });
    }
  };

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields();
      if (currentCategory) {
        // Structure data with translations
        const categoryData = {
          name: values.name,
          description: values.description,
          image: values.image,
          is_visible: values.is_visible ?? true,
          translations: [
            {
              type: "en",
              name: values.name_en || '',
              description: values.description_en || ''
            }
          ]
        };
        
        await categoryService.updateCategory(currentCategory._id, categoryData);
        setIsEditModalVisible(false);
        setCurrentCategory(null);
        editForm.resetFields();
        fetchCategories();
        enqueueSnackbar('Cập nhật danh mục thành công!', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Cập nhật danh mục thất bại!', { variant: 'error' });
    }
  };

  const handleCreateModalCancel = () => {
    setIsCreateModalVisible(false);
    createForm.resetFields();
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setCurrentCategory(null);
    editForm.resetFields();
  };

  const columns = [
    {
      title: 'Tên danh mục',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (text: string) => (
        <Tooltip title={`Tên danh mục: ${text}`}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Hình ảnh',
      dataIndex: 'image',
      key: 'image',
      align: 'center' as const,
      render: (imageUrl: string) => (
        <Tooltip title={imageUrl ? `Hình ảnh: ${imageUrl}` : 'Chưa có hình ảnh'}>
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt="category" 
              style={{ 
                width: '50px', 
                height: '50px', 
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }} 
            />
          ) : (
            <div style={{ 
              width: '50px', 
              height: '50px', 
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999'
            }}>
              No Image
            </div>
          )}
        </Tooltip>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      align: 'center' as const,
      render: (text: string) => (
        <Tooltip title={`Mô tả: ${text}`}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      align: 'center' as const,
      render: (text: string) => (
        <Tooltip title={`Slug: ${text}`}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center' as const,
      render: (text: string) => (
        <Tooltip title={`Ngày tạo: ${new Date(text).toLocaleString('vi-VN')}`}>
          <span>{new Date(text).toLocaleString('vi-VN')}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      align: 'center' as const,
      render: (text: string) => (
        <Tooltip title={`Ngày cập nhật: ${new Date(text).toLocaleString('vi-VN')}`}>
          <span>{new Date(text).toLocaleString('vi-VN')}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center' as const,
      render: (_: any, record: Category) => (
        <Space>
          <Tooltip title="Chỉnh sửa danh mục">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => showEditModal(record)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Xóa danh mục">
            <Popconfirm
              title="Xóa danh mục"
              description="Bạn có chắc chắn muốn xóa danh mục này?"
              onConfirm={() => handleDelete(record)}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Button 
                icon={<DeleteOutlined />} 
                danger 
                type="text"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      {/* Header row với bộ lọc */}
      <Card className="mb-4">
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Title level={2} className="m-0">
              Quản lý danh mục
            </Title>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              Thêm danh mục
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Bảng danh mục */}
      <Card>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="_id"
          loading={loading}
          pagination={false}
          bordered
        />
      </Card>

      <Modal
        title="Tạo danh mục mới"
        open={isCreateModalVisible}
        onOk={handleCreateModalOk}
        onCancel={handleCreateModalCancel}
        okText="Tạo mới"
        cancelText="Hủy"
        confirmLoading={loading}
      >
        <CategoryForm
          form={createForm}
        />
      </Modal>
      <Modal
        title="Chỉnh sửa danh mục"
        open={isEditModalVisible}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={loading}
      >
        <CategoryForm
          key={currentCategory?._id || 'new'}
          form={editForm}
          initialValues={currentCategory || undefined}
        />
      </Modal>
      <Modal
        title="Xác nhận xóa danh mục"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Xóa"
        okType="danger"
        cancelText="Hủy"
        confirmLoading={loading}
      >
        <p>Bạn có chắc chắn muốn xóa danh mục <b>"{categoryToDelete?.name}"</b>?</p>
      </Modal>
    </div>
  );
};

export default Categories;
